import { useRef } from 'react';
import { css } from '@linaria/core';
import { Box } from '@cbhq/cds-web/layout';
import { Pressable } from '@cbhq/cds-web/system';

import { variantGradient } from ':cloud/widgets/buildQuest/assets/colors';
import { hexagonPath } from ':cloud/widgets/buildQuest/assets/hexagon';
import { starPath, starStyles } from ':cloud/widgets/buildQuest/assets/star';
import { SVG_PROPS, TILE_HEIGHT, TILE_WIDTH } from ':cloud/widgets/buildQuest/constants';
import { TileProps } from ':cloud/widgets/buildQuest/types';
import { usePointsChip } from ':cloud/widgets/buildQuest/usePointsChip';

type BackgroundDivProps = {
  className?: string;
  mask?: string;
  children?: React.ReactNode;
};

function BackgroundDiv({
  className,
  mask,
  children,
  ...props
}: BackgroundDivProps): React.ReactElement {
  return (
    <div className={className} style={{ height: '100%', mask }} {...props}>
      {children}
    </div>
  );
}

export function UnlockedTile({
  id,
  variant,
  pointValue,
  onPress,
}: Pick<TileProps, 'id' | 'variant' | 'pointValue' | 'onPress'>): React.ReactElement {
  const pointsChipRef = useRef<HTMLDivElement | null>(null);
  const { pointsChipWidth } = usePointsChip(pointsChipRef);
  const pointsChipStrokeWidth = 3;
  const centeredPointsChipCoordinates = {
    x: TILE_WIDTH / 2 - pointsChipWidth / 2 - pointsChipStrokeWidth * 2,
    y: pointsChipStrokeWidth,
  };

  // 13px is the half of the line height of the display3 font
  const centeredTextCoordinates = { x: TILE_WIDTH / 2, y: TILE_HEIGHT / 2 + 13 };

  const pointsChipMaskId = `outlined-tile__pointsMask-${pointValue}`;

  const tileWrapper = css`
    .starWrapper {
      transition: all 0.4s cubic-bezier(0.6, 0, 0.15, 1);
    }
    &:hover .starWrapper {
      transform: translate(0px, -88px) scale(0.55);
    }
    .textWrapper {
      transition:
        transform 0.4s cubic-bezier(0.6, 0, 0.15, 1),
        opacity 0.3s cubic-bezier(0.6, 0, 0.15, 1);
      opacity: 0;
    }
    &:hover .textWrapper {
      opacity: 1;
    }
    .pointsWrapper {
      transition:
        transform 0.4s cubic-bezier(0.6, 0, 0.15, 1),
        opacity 0.3s cubic-bezier(0.6, 0, 0.15, 1);
      opacity: 0;
    }
    &:hover .pointsWrapper {
      transform: translate(0px, 85px);
      opacity: 1;
    }
  `;

  return (
    <Pressable
      onPress={() => {
        if (onPress) {
          onPress(id);
        }
      }}
      background="transparent"
    >
      <Box position="relative" className={tileWrapper}>
        <Box position="absolute" className="starWrapper">
          <svg {...SVG_PROPS}>
            <mask id="outlined-tile__starMask">
              <path d={starPath} fill="#fff" className={starStyles} />
            </mask>

            <foreignObject width={TILE_WIDTH} height={TILE_HEIGHT}>
              <BackgroundDiv
                className={variantGradient[variant]}
                mask="url(#outlined-tile__starMask)"
              />
            </foreignObject>
          </svg>
        </Box>

        <Box position="absolute" className="pointsWrapper" top="50%">
          <svg width="100%" height={36}>
            <mask id={pointsChipMaskId}>
              {/* Here we are trying to recreate the PointsChip component in vector form so we can use it as a mask */}
              <rect
                // account for the stroke width
                x={centeredPointsChipCoordinates.x}
                y={centeredPointsChipCoordinates.y}
                // recreating spacingHorizontal from the PointsChip component
                width={pointsChipWidth + 16}
                height="30" // 24px (text line height) + 3px (stroke width) + 3px (stroke width)
                strokeWidth="3"
                stroke="#fff"
                rx="14"
                ry="20"
              />
              <text
                textAnchor="middle"
                x="50%"
                y={23}
                fontFamily="var(--headline-font-family)"
                fontWeight="var(--headline-font-weight)"
                fontSize="var(--headline-font-size)"
                fill="#fff"
                // @ts-expect-error -- ref is technically not supported in SVG... but it works
                ref={pointsChipRef}
                key={`points-chip-${pointValue}`}
                width="100%"
              >
                +{pointValue.toLocaleString()}
              </text>
            </mask>

            <foreignObject width={TILE_WIDTH} height={TILE_HEIGHT}>
              <BackgroundDiv
                className={variantGradient[variant]}
                mask={`url(#${pointsChipMaskId})`}
              />
            </foreignObject>
          </svg>
        </Box>

        <Box className="textWrapper">
          <svg {...SVG_PROPS}>
            <mask id="outlined-tile__textMask">
              <text
                textAnchor="middle"
                x={centeredTextCoordinates.x}
                y={centeredTextCoordinates.y}
                fontFamily="var(--display3-font-family)"
                fontWeight="var(--display3-font-weight)"
                fontSize="var(--display3-font-size)"
                fill="#fff"
                key="text"
              >
                Begin
              </text>
            </mask>

            <foreignObject width={TILE_WIDTH} height={TILE_HEIGHT}>
              <BackgroundDiv
                className={variantGradient[variant]}
                mask="url(#outlined-tile__textMask)"
              />
            </foreignObject>
          </svg>
        </Box>

        <Box position="absolute">
          <svg {...SVG_PROPS}>
            <defs>
              <mask id="outlined-tile__strokeMask">
                <path d={hexagonPath} strokeWidth="3" stroke="#FFF" />
              </mask>
            </defs>

            <foreignObject width={TILE_WIDTH} height={TILE_HEIGHT}>
              <BackgroundDiv
                className={variantGradient[variant]}
                mask="url(#outlined-tile__strokeMask)"
              />
            </foreignObject>
          </svg>
        </Box>
      </Box>
    </Pressable>
  );
}
